'use client'

import { FC, useEffect, useRef, useState } from 'react'
import { Input } from '@dc-shared-web/components/shadcn'
import { cn } from '@dc-shared-web/lib'
import { formatCurrency } from '@dc-shared-web/lib/helpers'

export type InlineTextEditProps = {
  /** Whether the text can be edited. If false, displays as read-only text. */
  canEdit?: boolean
  /** The current text value to display and edit. */
  value: string
  /** Data test ID for testing purposes. */
  datatestid?: string
  /** Placeholder text shown when value is empty */
  placeholder?: string
  /** Additional CSS classes to apply to the component wrapper */
  className?: string
  /** Additional CSS classes to apply to the input element when editing. */
  inputClassName?: string
  /** Additional CSS classes to apply to the text when not editing. */
  savedClassName?: string
  /** Whether the field is required. Shows validation error if empty. */
  required?: boolean
  /** Callback function triggered when text value changes. */
  onValueChange?: (value: string) => void
  /** Whether the value is currently being saved. Shows loading state if true. */
  isSaving?: boolean
  /** Whether to format the value as currency with dollar sign. */
  useDollars?: boolean
}

export const InlineTextEdit: FC<InlineTextEditProps> = ({
  canEdit = true,
  datatestid,
  value,
  placeholder,
  className,
  inputClassName,
  savedClassName,
  required = false,
  onValueChange,
  isSaving,
  useDollars,
}) => {
  const elementRef = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState(false)
  const [editedValue, setEditedValue] = useState(value ?? '')
  const [displayValue, setDisplayValue] = useState('')

  const focusElement = () => {
    // without settimeout the first
    // blur event is not triggered
    setTimeout(() => {
      elementRef.current?.focus()
    }, 0)
  }

  const setCurrentValue = (newValue: string) => {
    elementRef.current && (elementRef.current.innerText = newValue)
    setEditedValue(newValue)
  }

  const handleFocus = () => {
    if (!canEdit || editing || isSaving) {
      return
    }

    setCurrentValue(editedValue ?? '')
    setEditing(true)
    focusElement()
  }

  const handleBlur = (save: boolean) => {
    if (save && (!required || editedValue)) {
      onValueChange?.(editedValue)
      setCurrentValue(editedValue || placeholder || '')
      elementRef.current?.blur()
    } else {
      setCurrentValue(value || placeholder || '')
    }

    setEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur(true)
    }

    // If user presses escape, cancel edit
    if (e.key === 'Escape') {
      handleBlur(false)
    }
  }

  const inputType = useDollars ? 'number' : 'text'

  useEffect(() => {
    if (!editedValue) {
      setDisplayValue(placeholder || '')
      return
    }

    if (!useDollars || editedValue === placeholder) {
      setDisplayValue(editedValue)
      return
    }

    setDisplayValue(formatCurrency(parseFloat(editedValue)))
  }, [editedValue, useDollars, placeholder])

  return (
    <>
      {editing ? (
        <Input
          datatestid={`${datatestid}-input`}
          type={inputType}
          value={editedValue}
          step={useDollars ? '0.01' : undefined}
          min={useDollars ? '0.01' : undefined}
          className={cn('tw-w-full', className)}
          inputClassName={cn(
            inputClassName,
            required && !editedValue
              ? 'focus-visible:tw-ring-red-500'
              : 'focus-visible:tw-ring-blue-600'
          )}
          onChange={(e) => setEditedValue(e.target.value)}
          onBlur={() => handleBlur(true)}
          ref={elementRef}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      ) : (
        <span
          data-testid={datatestid}
          className={cn(
            'tw-rounded tw-truncate tw-p-1 tw-max-w-[100%]',
            canEdit && !isSaving && 'hover:tw-bg-neutral-100',
            isSaving && 'tw-opacity-50 tw-cursor-not-allowed',
            className,
            value && savedClassName
          )}
          onClick={handleFocus}
        >
          {isSaving ? 'Saving...' : displayValue}
        </span>
      )}
    </>
  )
}
